import type { LC_ValidationError } from '@/api/shared';


// Flexible type for setError that works with React Hook Form
// Using any for the field parameter to match React Hook Form's UseFormSetError type
type SetErrorFunction = (field: any, options: { type: string; message: string }) => void;

// Type for form instances that can be any object with a setError method
interface FormInstanceWithSetError {
  setError: SetErrorFunction;
}

interface FieldMapping {
  [fieldName: string]: {
    formInstance: FormInstanceWithSetError;
  };
}

interface FieldToTabMapping<T extends string> {
  [fieldName: string]: {
    tab: T;
    formInstance: FormInstanceWithSetError;
  };
}

/**
 * Inject backend validation errors into form instances
 * 
 * Use case: Forms with independent endpoints per section
 * (e.g., update employee with 3 separate endpoints)
 * 
 * @param validationErrors - Array of validation errors from backend response
 * @param fieldMappingOrFormInstance - Map of field names to { formInstance }
 *   OR single form instance
 * 
 * @example
 * Single form instance:
 * ```ts
 * if (response.error?.code === 'VALIDATION_ERROR' && response.error.validationErrors) {
 *   injectValidationErrors(response.error.validationErrors, personalFormMethods);
 *   showNotification('error', 'Corrige los errores de validación', 4000);
 * }
 * ```
 * 
 * Multiple form instances:
 * ```ts
 * const fieldMapping = {
 *   email: { formInstance: personalFormMethods },
 *   phone: { formInstance: contactFormMethods },
 * };
 * injectValidationErrors(response.error.validationErrors, fieldMapping);
 * ```
 */
export function injectValidationErrors(
  validationErrors: LC_ValidationError[],
  fieldMappingOrFormInstance: FormInstanceWithSetError | FieldMapping,
): void {
  // Check if it's a single form instance (has setError method and is a function)
  if (
    'setError' in fieldMappingOrFormInstance &&
    typeof fieldMappingOrFormInstance.setError === 'function'
  ) {
    // Single form instance case
    const formInstance = fieldMappingOrFormInstance as FormInstanceWithSetError;
    validationErrors.forEach((validationError) => {
      formInstance.setError(validationError.fieldName, {
        type: 'manual',
        message: validationError.errorMessage,
      });
    });
  } else {
    // Field mapping case
    const fieldMapping = fieldMappingOrFormInstance as FieldMapping;
    validationErrors.forEach((validationError) => {
      const mapping = fieldMapping[validationError.fieldName];
      if (mapping) {
        mapping.formInstance.setError(validationError.fieldName, {
          type: 'manual',
          message: validationError.errorMessage,
        });
      }
    });
  }
}

/**
 * Process backend validation errors for multi-tab forms with automatic tab navigation
 * 
 * Features:
 * - Injects errors into corresponding React Hook Form instances
 * - Automatically navigates to the first tab containing validation errors (array order)
 * - Works with any number of tabs and forms
 * 
 * Use cases:
 * - Wizard forms (submit at end, single endpoint)
 * - Multi-tab forms with single endpoint (submit from any tab)
 * - Any tabbed form requiring backend validation error handling
 * 
 * @param validationErrors - Array of validation errors from backend response
 * @param fieldMapping - Map of field names to { tab, formInstance }
 * @param setActiveTab - Function to change the active tab
 * 
 * @example
 * ```ts
 * const fieldMapping = {
 *   rutComplete: { tab: 'personal' as const, formInstance: personalFormMethods },
 *   firstName: { tab: 'personal' as const, formInstance: personalFormMethods },
 *   email: { tab: 'contact' as const, formInstance: contactFormMethods },
 * };
 * 
 * if (response.error?.code === 'VALIDATION_ERROR' && response.error.validationErrors) {
 *   processBackendValidationErrors(
 *     response.error.validationErrors,
 *     fieldMapping,
 *     setActiveTab
 *   );
 * }
 * ```
 */
export function processBackendValidationErrors<T extends string>(
  validationErrors: LC_ValidationError[],
  fieldMapping: FieldToTabMapping<T>,
  setActiveTab: (tab: T) => void,
): void {
  let firstErrorTab: T | null = null;

  validationErrors.forEach((validationError) => {
    const mapping = fieldMapping[validationError.fieldName];

    // Skip unknown fields
    if (!mapping) {
      return;
    }

    // Track first tab with errors (first encountered in array order)
    if (!firstErrorTab) {
      firstErrorTab = mapping.tab;
    }

    // Inject error into corresponding React Hook Form instance
    mapping.formInstance.setError(validationError.fieldName, {
      type: 'manual',
      message: validationError.errorMessage,
    });
  });

  // Navigate to first tab containing validation errors
  if (firstErrorTab) {
    setActiveTab(firstErrorTab);
  }
}

